import {formatError} from './format-error'
import {relayFailureDisposition, relayFailureFromError, type RelayFailure} from './relay-failure'
import {asAhpVersionMismatch} from './protocol-support'
import type {ProvisioningStatus} from './session-host/resolve-project'

interface RelayTerminalStateDeps {
  logger: {error(message: string): void}
  onProvisioning?: (status: ProvisioningStatus) => void
  teardownClient: () => Promise<void>
  setStatus: (status: 'error', error: string, failure: RelayFailure) => void
  setClientEverConnected: (clientEverConnected: false) => void
}

export class RelayTerminalState {
  private readonly deps: RelayTerminalStateDeps

  /**
   * Set once a connection attempt fails with a permanent, non-retryable
   * condition (a deliberate host `RpcError`, a protocol-version mismatch, or a
   * terminal provisioning failure). The runtime then holds a terminal `error`
   * status and stops the reconnect loop until it is disposed.
   */
  private terminated = false

  constructor(deps: RelayTerminalStateDeps) {
    this.deps = deps
  }

  isTerminated(): boolean {
    return this.terminated
  }

  /**
   * Handle a failed (re)establish. Returns `true` when the failure is terminal
   * — surfaced as an `error` status with no reconnect — so callers skip their
   * retry path. Terminal conditions are a protocol-version mismatch, a terminal
   * provisioning failure, or any deliberate host `RpcError` outside the
   * retryable allowlist.
   */
  async handleTerminalEstablishError(err: unknown): Promise<boolean> {
    const failure = relayFailureFromError(err)
    if (relayFailureDisposition(failure).automaticRetry !== 'stop') return false
    const message =
      failure.kind === 'protocol-version' ? (asAhpVersionMismatch(err)?.message ?? formatError(err)) : formatError(err)
    this.terminated = true
    this.deps.setClientEverConnected(false)
    if (failure.kind === 'provisioning') {
      this.deps.logger.error(`relay project provisioning failed: ${message}`)
    } else {
      this.deps.logger.error(`relay connect failed (terminal): ${message}`)
    }
    // A partially-established connection may have already assigned an open
    // transport/client; release it before settling into the terminal `error`
    // state, since no later reconnect will run to clean it up.
    await this.deps.teardownClient()
    this.deps.setStatus('error', message, failure)
    if (failure.kind === 'provisioning') this.deps.onProvisioning?.({phase: 'failed', error: message})
    return true
  }
}
